import React, { useState, FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, User, Loader2, AlertCircle, Eye, EyeOff, ArrowRight } from "lucide-react";
import { homeFor, useAuth } from "../context/AuthContext";
import { getErrorMessage } from "../utils/apiError";

export default function Login() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const { login } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const from = (location.state as { from?: string } | null)?.from;

    const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        try {
            const user = await login(username.trim(), password);
            nav(from && from !== "/" ? from : homeFor(user.role), { replace: true });
        } catch (err) {
            setError(getErrorMessage(err, "Connection failed. Please try again."));
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#020617] relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#8b5cf6]/20 blur-[120px] rounded-full" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-[#8b5cf6]/10 blur-[100px] rounded-full" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-[440px] px-6"
            >
                <div className="bg-slate-900/40 backdrop-blur-2xl border border-slate-800 rounded-3xl shadow-2xl p-8 md:p-10">
                    <header className="flex flex-col items-center mb-10">
                        <motion.div whileHover={{ scale: 1.05 }} className="flex justify-center w-full">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                className="w-[70%] object-contain drop-shadow-[0_0_15px_rgba(139,92,246,0.5)]"
                            />
                        </motion.div>
                        <h1 className="text-2xl font-bold text-white tracking-tight mt-4">Welcome Back</h1>
                        <p className="text-slate-400 text-sm mt-1">Please enter your details to sign in</p>
                    </header>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="mb-6 flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                            >
                                <AlertCircle size={18} className="shrink-0" />
                                <span>{error}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleLogin} className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-widest ml-1">Username</label>
                            <div className="relative group">
                                <User
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#8b5cf6] transition-colors"
                                    size={19}
                                />
                                <input
                                    required
                                    autoFocus
                                    type="text"
                                    value={username}
                                    autoComplete="username"
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-slate-950/50 border border-slate-800 text-white pl-12 pr-4 py-3.5 rounded-2xl focus:ring-2 focus:ring-[#8b5cf6]/40 focus:border-[#8b5cf6] outline-none transition-all placeholder:text-slate-600"
                                    placeholder="Enter username"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-widest ml-1">Password</label>
                            <div className="relative group">
                                <Lock
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#8b5cf6] transition-colors"
                                    size={19}
                                />
                                <input
                                    required
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    autoComplete="current-password"
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-950/50 border border-slate-800 text-white pl-12 pr-12 py-3.5 rounded-2xl focus:ring-2 focus:ring-[#8b5cf6]/40 focus:border-[#8b5cf6] outline-none transition-all placeholder:text-slate-600"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors p-1"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <button
                            disabled={isLoading}
                            type="submit"
                            className="w-full group bg-[#8b5cf6] hover:bg-[#7c3aed] text-white font-bold py-4 rounded-2xl shadow-xl shadow-[#8b5cf6]/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                        >
                            <div className="flex items-center justify-center gap-2">
                                {isLoading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <>
                                        Sign In
                                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </div>
                        </button>
                    </form>

                    <footer className="mt-8 text-center border-t border-slate-800 pt-6">
                        <p className="text-slate-500 text-xs">Accounts are created by your administrator.</p>
                    </footer>
                </div>
            </motion.div>
        </div>
    );
}